import { useMemo, useState } from 'react';
import { isCompletedCode } from '../../../utils/statusColor';

export const INITIAL_FILTERS = {
  search: '',
  formIds: [],
  userIds: [],
  statusIds: [],
  dateFrom: '',
  dateTo: '',
  showCompleted: false,
};

// Gönderim listesi ve kanban görünümü tarafından paylaşılan filtre durumu ve süzme mantığı.
// Tamamlandı statüsündeki gönderimler, açıkça filtrelenmedikçe ya da "Tamamlananları göster"
// açılmadıkça varsayılan olarak sonuçlardan (pasif) tutulur.
export function useSubmissionFilters(submissions, forms, statuses) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const completedStatusId = useMemo(
    () => (statuses || []).find((status) => isCompletedCode(status.code))?.id ?? null,
    [statuses],
  );

  const formOptions = useMemo(() => {
    const map = new Map();
    (forms || []).forEach((form) => map.set(form.id, form.name));
    (submissions || []).forEach((item) => {
      if (!map.has(item.formId)) map.set(item.formId, item.formName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [forms, submissions]);

  const userOptions = useMemo(() => {
    const map = new Map();
    (submissions || []).forEach((item) => {
      if (!map.has(item.userId)) map.set(item.userId, item.userName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [submissions]);

  const filteredSubmissions = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const fromTs = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null;
    const toTs = filters.dateTo ? new Date(filters.dateTo).getTime() + 86_400_000 : null; // bitiş günü dahil

    return (submissions || []).filter((item) => {
      if (search) {
        const haystack = `${item.formName || ''} ${item.userName || ''} ${item.status || ''}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (filters.formIds.length > 0 && !filters.formIds.includes(item.formId)) return false;
      if (filters.userIds.length > 0 && !filters.userIds.includes(item.userId)) return false;
      if (filters.statusIds.length > 0 && !filters.statusIds.includes(item.statusId)) return false;
      const ts = item.submittedAt ? new Date(item.submittedAt).getTime() : 0;
      if (fromTs && ts < fromTs) return false;
      if (toTs && ts > toTs) return false;

      // Tamamlandı statüsü: açıkça statü filtresiyle seçilmedikçe ve "göster" açılmadıkça gizli
      if (completedStatusId != null && item.statusId === completedStatusId) {
        const explicitlySelected = filters.statusIds.includes(completedStatusId);
        if (!filters.showCompleted && !explicitlySelected) return false;
      }

      return true;
    });
  }, [filters, submissions, completedStatusId]);

  const activeFilterCount =
    (filters.search ? 1 : 0) +
    (filters.formIds.length > 0 ? 1 : 0) +
    (filters.userIds.length > 0 ? 1 : 0) +
    (filters.statusIds.length > 0 ? 1 : 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.showCompleted ? 1 : 0);

  function toggleMulti(key, value) {
    setFilters((prev) => {
      const list = prev[key].includes(value) ? prev[key].filter((entry) => entry !== value) : [...prev[key], value];
      return { ...prev, [key]: list };
    });
  }

  function resetFilters() {
    setFilters(INITIAL_FILTERS);
  }

  return {
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    formOptions,
    userOptions,
    filteredSubmissions,
    activeFilterCount,
    toggleMulti,
    resetFilters,
    completedStatusId,
  };
}
